import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Camera, CheckCircle, ArrowRight } from 'lucide-react';

const FieldTests = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const testResults = [
    { metric: 'Interception Rate', value: '98.7%', status: 'Excellent' },
    { metric: 'Response Time', value: '<3 sec', status: 'Verified' },
    { metric: 'Multi-Target Tracking', value: '12 simultaneous', status: 'Operational' },
    { metric: 'Weather Performance', value: 'All conditions', status: 'Tested' }
  ];

  const scenarios = [
    {
      title: 'Urban Environment Test',
      location: 'Tallinn Test Range, Estonia',
      date: 'November 2024',
      description: 'High-density urban simulation with multiple drone incursions from different vectors. Wolf Eye successfully intercepted 15/15 targets.',
      image: 'https://images.unsplash.com/photo-1473163928189-364b2c4e1135?w=800&q=80'
    },
    {
      title: 'Border Defense Scenario',
      location: 'NATO Exercise - Poland',
      date: 'October 2024',
      description: 'Extended 50km defense line test. Modules coordinated autonomously to create seamless coverage with zero gaps.',
      image: 'https://images.unsplash.com/photo-1577495508048-b635879837f1?w=800&q=80'
    },
    {
      title: 'Critical Infrastructure Protection',
      location: 'Power Plant Security Test',
      date: 'September 2024',
      description: 'Simulated attack on energy facility. Wolf Eye neutralized swarm attack of 8 drones within 90 seconds.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&q=80'
    }
  ];

  return (
    <div className="dark-page defense-page">
      {/* Hero */}
      <section className="defense-hero" style={{ minHeight: '60vh' }}>
        <div className="defense-hero-content" style={{ padding: '80px 0' }}>
          <div style={{ maxWidth: '1000px' }}>
            <div className="classification-badge" style={{ marginTop: '20px' }}>FIELD VALIDATION</div>
            <h1 className="defense-headline" style={{ fontSize: '64px', marginTop: '24px' }}>Field Test Results</h1>
            <h2 className="defense-subheadline" style={{ marginTop: '20px' }}>Real-World Performance Data</h2>
            <p className="defense-description" style={{ maxWidth: '800px', marginTop: '40px' }}>
              Comprehensive field testing across diverse operational environments. All tests conducted under NATO observation protocols.
            </p>
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="capabilities-section">
        <div className="section-tag">VERIFIED PERFORMANCE</div>
        <h2 className="section-title-large">Test Results Summary</h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', maxWidth: '1400px', margin: '0 auto' }}>
          {testResults.map((result, index) => (
            <div key={index} style={{ padding: '32px', background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#666666', textTransform: 'uppercase', marginBottom: '12px' }}>{result.metric}</div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#D4AF37', marginBottom: '12px' }}>{result.value}</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)' }}>
                <CheckCircle size={16} style={{ color: '#D4AF37' }} />
                {result.status}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Test Scenarios */}
      <section className="configs-section">
        <div className="section-tag">OPERATIONAL SCENARIOS</div>
        <h2 className="section-title-large">Field Test Documentation</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '60px', maxWidth: '1200px', margin: '60px auto 0' }}>
          {scenarios.map((scenario, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: index % 2 === 0 ? '1fr 1fr' : '1fr 1fr', gap: '40px', alignItems: 'center' }}>
              <div style={{ order: index % 2 === 0 ? 1 : 2 }}>
                <img 
                  src={scenario.image} 
                  alt={scenario.title}
                  style={{ width: '100%', height: '300px', objectFit: 'cover', border: '1px solid rgba(212, 175, 55, 0.2)' }}
                />
              </div>
              <div style={{ order: index % 2 === 0 ? 2 : 1 }}>
                <div style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
                  {scenario.date} • {scenario.location}
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF', marginBottom: '16px' }}>
                  {scenario.title}
                </h3>
                <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8' }}>
                  {scenario.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Video Section */}
      <section className="capabilities-section" style={{ background: '#0A0A0A' }}>
        <div className="section-tag">VIDEO DOCUMENTATION</div>
        <h2 className="section-title-large">Live Interception Footage</h2>
        
        <div style={{ maxWidth: '1000px', margin: '60px auto 0', textAlign: 'center' }}>
          <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000000', border: '2px solid rgba(212, 175, 55, 0.2)' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
              <Play size={64} style={{ color: '#D4AF37', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>Field test video available upon request</p>
              <p style={{ fontSize: '13px', color: '#666666', marginTop: '8px' }}>Contact our team for secure access</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certification */}
      <section className="capabilities-section">
        <div className="section-tag">INDEPENDENT VERIFICATION</div>
        <h2 className="section-title-large">Third-Party Validation</h2>
        
        <div style={{ maxWidth: '900px', margin: '60px auto 0', padding: '48px', background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
          <p style={{ fontSize: '18px', lineHeight: '1.8', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '32px' }}>
            All field tests conducted under NATO supervision and documented according to STANAG 4586 protocols. Results verified by independent defense technology assessment teams.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(212, 175, 55, 0.05)' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37' }}>NATO</div>
              <div style={{ fontSize: '12px', color: '#666666', marginTop: '8px' }}>Observed Testing</div>
            </div>
            <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(212, 175, 55, 0.05)' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37' }}>STANAG 4586</div>
              <div style={{ fontSize: '12px', color: '#666666', marginTop: '8px' }}>Protocol Compliant</div>
            </div>
            <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(212, 175, 55, 0.05)' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37' }}>ISO 9001</div>
              <div style={{ fontSize: '12px', color: '#666666', marginTop: '8px' }}>Quality Certified</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2 className="cta-headline">Request Detailed Test Reports</h2>
          <p className="cta-subtext">
            Access comprehensive field test documentation and performance data.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary-large">
              <span>Contact Us</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FieldTests;
